import { useState } from "react";
import { alpha, Box, Button, Container, Divider, Grid, Typography, useTheme } from "@mui/material";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { StyledSlider } from "@components/CustomSlider";
import { InactivityHandler } from "@components/InactivityHandler";
import { setLoanStatus } from "@store/slices/registerSlice";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";

interface OfferScenario {
  crop: string;
  region: string;
  cycle: string;
  regime: string;
  minHectares: number;
  maxHectares: number;
  defaultHectares: number;
  creditQuotaPerHectare: number;
  creditPaymentPerHectare: number;
  maturityDate: string;
  rateLabel: string;
  adminCommissionLabel: string;
  dispositionCommissionLabel: string;
}

// TODO: reemplazar por datos reales de condiciones/agrocostos cuando esté disponible el endpoint.
const MOCK_SCENARIOS: OfferScenario[] = [
  {
    crop: "maíz",
    region: "Tamaulipas Norte",
    cycle: "OI 2025/2026",
    regime: "Riego",
    minHectares: 25,
    maxHectares: 75,
    defaultHectares: 50,
    creditQuotaPerHectare: 20068,
    creditPaymentPerHectare: 26083,
    maturityDate: "2026-08-15",
    rateLabel: "TIIE 28d + 10.30",
    adminCommissionLabel: "2% + IVA",
    dispositionCommissionLabel: "2% + IVA",
  },
  {
    crop: "sorgo",
    region: "Sinaloa Sur",
    cycle: "PV 2026",
    regime: "Temporal",
    minHectares: 40,
    maxHectares: 120,
    defaultHectares: 80,
    creditQuotaPerHectare: 15500,
    creditPaymentPerHectare: 19800,
    maturityDate: "2026-11-20",
    rateLabel: "TIIE 28d + 9.80",
    adminCommissionLabel: "1.5% + IVA",
    dispositionCommissionLabel: "1.5% + IVA",
  },
];

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const formatDueDate = (iso: string) => {
  const [year, month, day] = iso.split("T")[0].split("-").map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

const formatMoney = (value: number) => `$${Math.round(value).toLocaleString("es-MX")}`;

const OfertaAvicola = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const application = useSelector((state: RootState) => state.register.application);

  const scenario = MOCK_SCENARIOS[1];
  const [hectares, setHectares] = useState(scenario.defaultHectares);

  const creditAmount = Math.round(scenario.creditQuotaPerHectare * hectares);
  const totalProjectCost = Math.round(scenario.creditPaymentPerHectare * hectares);
  const producerContribution = totalProjectCost - creditAmount;

  const summaryFields = [
    { label: "Tasa anual", value: scenario.rateLabel },
    { label: "Comisión administración", value: scenario.adminCommissionLabel },
    { label: "Comisión disposición", value: scenario.dispositionCommissionLabel },
    { label: "Costo total del proyecto", value: formatMoney(totalProjectCost) },
    { label: "Aportación del productor", value: formatMoney(producerContribution) },
    { label: "Vencimiento", value: formatDueDate(scenario.maturityDate) },
  ];

  const handleContinue = () => {
    const { check_list, informacion_documentos_pfae } = STATUS_CATALOG;

    dispatch(setLoanStatus(check_list.code));

    if (application.tax_system_type === TAX_SYSTEM_TYPE.PM.code) {
      navigate(check_list.url);
    } else {
      navigate(informacion_documentos_pfae.url);
    }
  };

  const mutedDark = alpha(theme.palette.primary.dark, 0.6);
  const borderColor = alpha(theme.palette.primary.dark, 0.12);

  return (
    <Grid container>
      <Grid item xs={12} md={12} mt={5} sx={{ position: "relative" }}>
        <InactivityHandler />
        <Container maxWidth={"sm"}>
          <Box sx={{ mx: 2, mb: 6 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Box width={4} height={16} bgcolor="primary.main" borderRadius={1} />
              <Typography
                variant="overline"
                color="primary.main"
                fontWeight={theme?.palette?.fontWeights?.bold}
                sx={{ letterSpacing: "0.5px" }}
              >
                Decisión · Pre-autorización
              </Typography>
            </Box>

            <Typography
              variant="h4"
              color="primary.dark"
              fontWeight={theme?.palette?.fontWeights?.bold}
              fontSize={{ xs: "24px", sm: "28px" }}
              mb={1.5}
            >
              ¡Tu crédito de avío está pre-autorizado!
            </Typography>

            <Typography variant="body1" color="primary.dark" sx={{ opacity: 0.75, lineHeight: 1.6 }} mb={4}>
              Autorizamos financiamiento para{" "}
              <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                {scenario.crop}
              </Box>{" "}
              en{" "}
              <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                {scenario.region}
              </Box>
              , ciclo{" "}
              <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                {scenario.cycle}
              </Box>{" "}
              · {scenario.regime}. Ajusta la superficie a financiar dentro del rango autorizado.
            </Typography>

            <Box
              sx={{
                border: `1px solid ${borderColor}`,
                borderRadius: "20px",
                p: { xs: 2.5, sm: 3.5 },
                mb: 2.5,
                bgcolor: "common.white",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5} gap={2}>
                <Typography fontWeight={theme?.palette?.fontWeights?.semibold} color="primary.dark">
                  Superficie a financiar
                </Typography>
                <Typography variant="body2" textAlign="right" sx={{ color: mutedDark }}>
                  Autorizado · {scenario.minHectares} a {scenario.maxHectares} ha
                </Typography>
              </Box>

              <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                <Typography
                  fontWeight={theme?.palette?.fontWeights?.bold}
                  color="primary.dark"
                  fontSize={{ xs: 36, sm: 44 }}
                >
                  {hectares}
                </Typography>
                <Typography fontSize={18} sx={{ color: mutedDark }}>
                  ha
                </Typography>
              </Box>

              <StyledSlider
                aria-label="superficie"
                value={hectares}
                min={scenario.minHectares}
                max={scenario.maxHectares}
                step={1}
                onChange={(_, value) => setHectares(value as number)}
              />

              <Box display="flex" justifyContent="space-between" mt={-0.5} mb={2}>
                <Typography variant="body2" sx={{ color: mutedDark }}>
                  {scenario.minHectares} ha
                </Typography>
                <Typography variant="body2" sx={{ color: mutedDark }}>
                  {scenario.maxHectares} ha
                </Typography>
              </Box>

              <Divider sx={{ borderColor, mb: 2 }} />

              <Typography variant="body2" sx={{ color: mutedDark }}>
                Cuota de crédito FIRA · {formatMoney(scenario.creditQuotaPerHectare)} / ha
              </Typography>
            </Box>

            <Box
              sx={{
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.base, 0.9)} 0%, ${alpha(theme.palette.primary.base, 0.35)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                borderRadius: "20px",
                p: { xs: 2.5, sm: 3.5 },
                mb: 2.5,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                <Typography
                  variant="body2"
                  fontWeight={theme?.palette?.fontWeights?.bold}
                  sx={{ color: mutedDark, textTransform: "uppercase", letterSpacing: "0.4px" }}
                >
                  Monto de crédito · {hectares} ha
                </Typography>

                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  sx={{ bgcolor: "common.white", borderRadius: "999px", px: 1.5, py: 0.5, flexShrink: 0 }}
                >
                  <Box width={7} height={7} borderRadius="50%" bgcolor="primary.main" />
                  <Typography variant="caption" fontWeight={theme?.palette?.fontWeights?.bold} color="primary.main">
                    Pre-autorizado
                  </Typography>
                </Box>
              </Box>

              <Typography component="div" color="primary.dark" fontWeight={theme?.palette?.fontWeights?.bold} mb={1}>
                <Box component="span" sx={{ fontSize: { xs: "32px", sm: "38px" }, lineHeight: 1 }}>
                  {formatMoney(creditAmount)}
                </Box>{" "}
                <Box component="span" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  MXN
                </Box>
              </Typography>

              <Typography variant="body2" mb={2.5} sx={{ color: mutedDark }}>
                Capital + intereses al vencimiento · {formatDueDate(scenario.maturityDate)} · Sujeto a dictamen técnico
                del predio
              </Typography>

              <Divider sx={{ borderColor: alpha(theme.palette.primary.dark, 0.15), mb: 2.5 }} />

              <Grid container rowSpacing={2.5} columnSpacing={2}>
                {summaryFields.map((field) => (
                  <Grid item xs={6} key={field.label}>
                    <Typography
                      variant="caption"
                      sx={{ color: mutedDark, textTransform: "uppercase", letterSpacing: "0.3px", display: "block" }}
                    >
                      {field.label}
                    </Typography>
                    <Typography fontWeight={theme?.palette?.fontWeights?.semibold} color="primary.dark">
                      {field.value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.base, 0.6),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: "14px",
                p: 2,
                display: "flex",
                gap: 1.25,
                mb: 3,
              }}
            >
              <InfoOutlinedIcon sx={{ color: "primary.main", fontSize: 20, mt: "1px", flexShrink: 0 }} />
              <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.85, lineHeight: 1.55 }}>
                Esta pre-autorización no constituye una autorización definitiva del crédito. Está sujeta al cumplimiento
                de todos los requisitos de formalización y contratación, y las condiciones podrían cambiar según el
                dictamen técnico del predio y la situación vigente al momento de la contratación.
              </Typography>
            </Box>

            <Divider sx={{ borderColor, mb: 2.5 }} />

            <Box display="flex" alignItems="flex-end" gap={1.5}>
              <Button variant="default" onClick={handleContinue}>
                Continuar al expediente →
              </Button>
            </Box>
          </Box>
        </Container>
      </Grid>
    </Grid>
  );
};

export default OfertaAvicola;
